import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, Section, Kpi, Pill } from '../components/ui-kit';
import { Copy } from 'lucide-react';

export default function Referral() {
  const { getToken } = useXyqarAuth();
  const [r, setR] = React.useState<{ code?: string; link?: string; signed_up?: number; converted?: number; earned_eur?: number }>({});

  React.useEffect(() => {
    (async () => {
      try { const tok = await getToken().catch(() => ''); setR(await apiGet('/api/v1/expert/referral', tok)); }
      catch { /* */ }
    })();
  }, [getToken]);

  const copy = () => r.link && navigator.clipboard.writeText(r.link);
  const signedUp = r.signed_up ?? 0;
  const converted = r.converted ?? 0;
  const conversionPct = signedUp > 0 ? Math.round((converted / signedUp) * 100) : 0;

  return (
    <div>
      <PageTitle kicker="Refer & earn" title="Your referral link" right={<Pill tone="live">5% first invoice</Pill>} />

      <Section title="At a glance" hint="Referral performance is based on sign-up and first-invoice conversion only.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Sign-ups" value={signedUp} />
          <Kpi accent="green" label="Converted" value={converted} />
          <Kpi accent="blue" label="Conversion" value={`${conversionPct}%`} />
          <Kpi label="Earned €" value={(r.earned_eur ?? 0).toLocaleString('fr-FR')} />
        </div>
      </Section>

      <Section title="Share link">
        <Card className="mb-6">
          <p className="text-xs text-white/60 mb-3">Share this link to onboard an expert or client and earn 5% on the first issued invoice.</p>
          <div className="flex gap-2">
            <input readOnly value={r.link ?? '—'} className="flex-1 rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm text-white font-mono" />
            <button onClick={copy} className="px-4 rounded-md border border-white/15 text-white/70 hover:text-white"><Copy className="h-4 w-4" /></button>
          </div>
        </Card>
      </Section>

      <Section title="How it works">
        <div className="grid gap-4 lg:grid-cols-3">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Step 1</p>
            <p className="text-sm text-white mt-3">Share the dedicated link.</p>
            <p className="text-xs text-white/60 mt-2">The referral tag is carried into onboarding automatically.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Step 2</p>
            <p className="text-sm text-white mt-3">Track sign-up and conversion.</p>
            <p className="text-xs text-white/60 mt-2">A conversion only counts once the referred account reaches invoiced activity.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Step 3</p>
            <p className="text-sm text-white mt-3">Collect referral earnings.</p>
            <p className="text-xs text-white/60 mt-2">Referral earnings are informational here; settlement follows the finance cycle.</p>
          </Card>
        </div>
      </Section>
    </div>
  );
}
function Stat({ label, value }: { label: string; value: number | string }) {
  return <Card><p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">{label}</p><p className="text-3xl text-white mt-2">{value}</p></Card>;
}
